"use client";

import clsx from "clsx";
import {useTranslation} from "react-i18next";
import {outcomeNotice, type ToolApprovalStatus} from "@repo/ui/gailgpt/tool-approval-status";
import Button from "@/components/library/Button";

interface ToolApprovalNoticesProps {
   status: ToolApprovalStatus;
   settled: boolean;
   expired: boolean;
   error: boolean;
   previewUnavailable?: boolean;
   /** The proposal is open, but only the conversation owner can decide it. */
   ownerOnly?: boolean;
   className?: string;
   canRequestFresh?: boolean;
   onRequestFresh?: () => Promise<void>;
   requestedFresh?: boolean;
}

/** Why an approval cannot be applied, or what happened after it was, for every review surface. */
export default function ToolApprovalNotices({status, settled, expired, error, previewUnavailable, ownerOnly, className, canRequestFresh, onRequestFresh, requestedFresh}: ToolApprovalNoticesProps) {
   const {t} = useTranslation("ask");
   const outcome = outcomeNotice(status);
   const notices = [
      !settled && previewUnavailable && {key: "previewUnavailable", tone: "warning"},
      !settled && expired && {key: "expiredDetail", tone: "warning"},
      !settled && !expired && ownerOnly && {key: "ownerOnly", tone: "info"},
      outcome && {key: outcome.notice, tone: outcome.tone},
      error && {key: "submitError", tone: "error"},
   ].filter((notice): notice is {key: string; tone: "info" | "warning" | "error"} => !!notice);
   return <>
      {notices.map(notice => <p key={notice.key} role={notice.tone === "info" ? "note" : "alert"} className={clsx(className, {
         "text-600": notice.tone === "info",
         "text-amber-700 dark:text-amber-300": notice.tone === "warning",
         "text-red-700 dark:text-red-300": notice.tone === "error",
      })}>
         {t(`toolApproval.${notice.key}`)}
      </p>)}
      {canRequestFresh && <div className={className}>
         <Button type="tertiary" compact onClick={() => void onRequestFresh?.()}>{t("toolApproval.requestFresh")}</Button>
      </div>}
      {requestedFresh && <output className={clsx("block", className)}>{t("toolApproval.freshDrafted")}</output>}
   </>;
}
